// ─────────────────────────────────────────────────────────────────────────────
// SOURCE OF TRUTH — only edit here when adding new permission modules or actions.
//
// To add a new module (e.g., "products"):
//   1. Add an entry to PERMISSION_MAP below with its allowed actions.
//   2. PERMISSIONS const and all derived types update automatically — done.
//
// To add a new action to an existing module (e.g., "export"):
//   1. Append the string to the module's array in PERMISSION_MAP.
//   2. Done — PERMISSIONS.CATALOG_EXPORT appears automatically.
//
// Format: "<entity>.<action>" — must match backend permission strings exactly.
// ─────────────────────────────────────────────────────────────────────────────
const PERMISSION_MAP = {
  catalog: ["read", "write", "admin", "import"],
  users: ["read", "write", "admin"],
  channels: ["read", "write", "admin"],
} as const;

type PermissionMap = typeof PERMISSION_MAP;

// "catalog" | "users" | "channels"
export type Entity = keyof PermissionMap;

// Valid actions per entity — ActionFor<"catalog"> → "read" | "write" | "admin" | "import"
export type ActionFor<E extends Entity> = PermissionMap[E][number];

// ─────────────────────────────────────────────────────────────────────────────
// Permission — template literal union of all valid "<entity>.<action>" strings.
// Invalid combinations (e.g. "users.import", "catalog.fly") do not exist in this type.
// TypeScript will error at compile time if you pass an invalid permission string.
// ─────────────────────────────────────────────────────────────────────────────
export type Permission = { [E in Entity]: `${E}.${ActionFor<E>}` }[Entity];

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSIONS const — auto-generated from PERMISSION_MAP, zero manual duplication.
//
// Usage:
//   PERMISSIONS.CATALOG_READ   → "catalog.read"
//   PERMISSIONS.USERS_WRITE    → "users.write"
//   PERMISSIONS.CHANNELS_ADMIN → "channels.admin"
//
// TypeScript knows every key and its exact literal string value.
// Accessing a non-existent key (e.g. PERMISSIONS.USERS_IMPORT) is a compile error.
// ─────────────────────────────────────────────────────────────────────────────
type PermissionsConst = {
  [E in Entity as `${Uppercase<E>}_${Uppercase<ActionFor<E>>}`]: `${E}.${ActionFor<E>}`;
};

const buildPermissions = (): PermissionsConst => {
  const result: Record<string, string> = {};

  for (const entity of Object.keys(PERMISSION_MAP) as Entity[]) {
    for (const action of PERMISSION_MAP[entity]) {
      result[`${entity.toUpperCase()}_${action.toUpperCase()}`] = `${entity}.${action}`;
    }
  }

  return result as PermissionsConst;
};

export const PERMISSIONS = buildPermissions();

// Mirror of PERMISSION_COVERS in so-pim libs/auth/src/constants/permissions.ts — keep in sync.
export const PERMISSION_COVERS: Readonly<Partial<Record<string, readonly Permission[]>>> = {
  "catalog.admin": ["catalog.admin", "catalog.write", "catalog.read", "catalog.import"],
  "catalog.write": ["catalog.write", "catalog.read", "catalog.import"],
  "catalog.read": ["catalog.read"],
  "catalog.import": ["catalog.import"],
  "users.admin": ["users.admin", "users.write", "users.read"],
  "users.write": ["users.write", "users.read"],
  "users.read": ["users.read"],
  "channels.admin": ["channels.admin", "channels.write", "channels.read"],
  "channels.write": ["channels.write", "channels.read"],
  "channels.read": ["channels.read"],
} satisfies Record<Permission, readonly Permission[]>;

// ─────────────────────────────────────────────────────────────────────────────
// Role constants — separate concept from permissions.
// A role determines a user's global access level; permissions are org-scoped.
//
// ADMIN is a "super role" — bypasses all permission checks (see permissions.utils.ts).
//
// When adding new roles:
//   1. Add to USER_ROLES below.
//   2. Review isUnrestrictedRole() in permissions.utils.ts — decide if it gets bypass.
//   3. Update route meta types in app/router/types/types.ts if needed.
// ─────────────────────────────────────────────────────────────────────────────
export const USER_ROLES = {
  ADMIN: "ADMIN",
  USER: "USER",
} as const satisfies Record<string, string>;

export type UserRoleValue = typeof USER_ROLES[keyof typeof USER_ROLES];
